import type {
  MessageContent,
  MessageFormatting,
  NativeMessageEntity,
  TextEdit,
} from "../types"
import { getTextEditError, mapEntityRanges } from "./entityRanges"
import { findSlotMarkers } from "./slots"

export type TextEditResult =
  | { ok: true; content: MessageContent }
  | { ok: false; content: MessageContent; reason: string; entityIndex?: number }

/**
 * Atomic source edit. Markup stays raw source in its original parse mode;
 * grammar validation and semantic visual edits belong to the markup codecs.
 * On failure, content is the original object, not a partially updated draft.
 */
export function applyTextEdit(
  content: MessageContent,
  edit: TextEdit,
): TextEditResult {
  const error = getTextEditError(edit, content.text.length)
  if (error) return { ok: false, content, reason: error }
  const noOp = edit.from === edit.to && edit.text.length === 0
  if (!noOp) {
    for (const marker of findSlotMarkers(content.text)) {
      const end = marker.offset + marker.length
      const intersects =
        edit.from === edit.to
          ? edit.from > marker.offset && edit.from < end
          : edit.from < end && edit.to > marker.offset
      const replacesWholeMarker = edit.from <= marker.offset && edit.to >= end
      if (intersects && !replacesWholeMarker) {
        return {
          ok: false,
          content,
          reason: "An edit must replace the whole slot marker, not split it",
        }
      }
    }
  }

  let formatting = content.formatting
  if (formatting.kind === "entities") {
    const result = mapEntityRanges(
      formatting.entities,
      edit,
      content.text.length,
    )
    if (!result.ok) return { ...result, content }
    formatting = { kind: "entities", entities: result.entities }
  }
  if (noOp) return { ok: true, content }
  return {
    ok: true,
    content: {
      ...content,
      text:
        content.text.slice(0, edit.from) +
        edit.text +
        content.text.slice(edit.to),
      formatting,
    },
  }
}

type SourceFields = Pick<MessageContent, "text" | "mediaIds" | "buttons">
export type SerializedMessageFields = SourceFields &
  (
    | { parseMode?: never; entities?: never }
    | {
        parseMode: Extract<MessageFormatting, { kind: "markup" }>["parseMode"]
        entities?: never
      }
    | { entities: NativeMessageEntity[]; parseMode?: never }
  )

/** Explicit source fields only: never spread presentation data or a DTO. */
export function serializeMessageFields(
  content: MessageContent,
): SerializedMessageFields {
  const fields: SourceFields = {
    text: content.text,
    mediaIds: content.mediaIds,
    buttons: content.buttons,
  }
  switch (content.formatting.kind) {
    case "plain":
      return fields
    case "markup":
      return { ...fields, parseMode: content.formatting.parseMode }
    case "entities":
      return { ...fields, entities: content.formatting.entities }
  }
}
